import { Box, Text } from '@chakra-ui/react';
import 'css-skeletons/dist/css-skeletons.min.css';

import mapboxgl, { type Map as MapboxMap } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useKibanaLogger } from '../../../../../shared/hooks/kibana-logger/kibana-logger';
import { useSelectionLists } from '../../../../../shared/selection-lists/hooks/useSelectionLists';
import type { SelectionListItem } from '../../../../../shared/selection-lists/types';
import { useFilters } from '../../../filters/hooks/useFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import { PageType } from '../../PageConfigProvider/types';
import type {
  DataNormalization,
  DataTransformation,
} from '../../PageConfigProvider/types/item';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import { LAYER_CONFIGS } from './ChoroplethRenderer/ChoroplethConfig';
import { ChoroplethRenderer } from './ChoroplethRenderer/ChoroplethRenderer';
import { createChoroplethFeatureBuilder } from './ChoroplethRenderer/choroplethFeatureBuilder';
import { computeExtent } from './ChoroplethRenderer/dataManager';
import styles from './GenericMap.module.css';
import './custom-mapbox.css';

import { useOverviewTooltip } from './hooks/useOverviewTooltip';
import { useSyncViewport } from './hooks/useSyncViewport';
import { useTooltipPortal } from './hooks/useTooltipPortal';
import './popup-css.css';

import { LocationType, type MapPoint, type MapRenderer } from './types';
import { calculateBounds, isCenterZoomBounds } from './utils/calculate-bounds';
import { getMaxZoom, MIN_ZOOM } from './utils/constants';
import {
  getNormalizedGeographySelections,
  hasActiveGeographyFilters,
} from './utils/geography-adapter';

interface WebGLDiagnostics {
  contextType: 'webgl2' | 'webgl' | 'experimental-webgl' | 'none';
  vendor: string;
  renderer: string;
  hasDebugRendererInfo: boolean;
  errorMessage?: string;
}

function getWebGLDiagnostics(): WebGLDiagnostics {
  const canvas = document.createElement('canvas');
  const contextTypes = ['webgl2', 'webgl', 'experimental-webgl'] as const;
  let creationErrorMessage = '';

  const onCreationError = (e: Event) => {
    const raw =
      'statusMessage' in e
        ? (e as { statusMessage: unknown }).statusMessage
        : undefined;
    const msg = typeof raw === 'string' ? raw : undefined;
    if (msg && !creationErrorMessage) {
      creationErrorMessage = msg;
    }
  };

  canvas.addEventListener('webglcontextcreationerror', onCreationError);

  for (const contextType of contextTypes) {
    const gl = canvas.getContext(contextType) as WebGLRenderingContext | null;
    if (gl) {
      canvas.removeEventListener('webglcontextcreationerror', onCreationError);
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

      const vendor = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : gl.getParameter(gl.VENDOR);

      const renderer = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : gl.getParameter(gl.RENDERER);

      return {
        contextType,
        vendor: vendor ? String(vendor) : 'unknown',
        renderer: renderer ? String(renderer) : 'unknown',
        hasDebugRendererInfo: !!debugInfo,
      };
    }
  }

  canvas.removeEventListener('webglcontextcreationerror', onCreationError);

  return {
    contextType: 'none',
    vendor: 'unknown',
    renderer: 'unknown',
    hasDebugRendererInfo: false,
    errorMessage:
      creationErrorMessage ||
      'WebGL is not supported or hardware acceleration is disabled.',
  };
}

const MAPBOX_TOKEN =
  'pk.eyJ1IjoicmV2ZWxpb2xhYnMiLCJhIjoiY2w3N3pjM2tlMDRuczN2cXB1ZTU3bWZvdyJ9.AluK-n7WOv2tAOhTL-ZzAQ';
mapboxgl.accessToken = MAPBOX_TOKEN;

const WORLD_BOUNDS = {
  minLon: -180,
  minLat: -75,
  maxLon: 180,
  maxLat: 85,
};

function clampZoom(zoom: number, minZoom: number, maxZoom: number): number {
  return Math.max(minZoom, Math.min(maxZoom, zoom));
}

export const GenericMap = ({
  points,
  metric,
  syncViewport = true,
  normalization,
  transformation,
  primaryDimension,
  interactive = true,
  isSourceReady,
  'data-testid': testId,
}: {
  points: MapPoint[];
  metric: MetricValues;
  syncViewport?: boolean;
  normalization?: DataNormalization;
  transformation?: DataTransformation;
  primaryDimension?: PrimaryFilterId;
  interactive?: boolean;
  isSourceReady?: (isReady: boolean) => void;
  'data-testid'?: string;
}) => {
  const { config: pageConfig } = usePageConfig();
  const isReport = pageConfig.type === PageType.REPORT;
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const rendererRef = useRef<MapRenderer | null>(null);

  const isApplyingRemoteViewport = useRef(false);
  const mapIdRef = useRef<string>(
    `generic-map-${Math.random().toString(36).slice(2, 9)}`
  );
  const [isMapReady, setIsMapReady] = useState(false);
  const [webGLError, setWebGLError] = useState<string | null>(null);
  const { kibanaLogger } = useKibanaLogger();

  const geographyMode = useGeographyMode();

  const selectionListKeys = (
    geographyMode === 'administrative' ? ['state', 'city'] : ['msa']
  ) as ('msa' | 'state' | 'city')[];
  const selectionLists = useSelectionLists(selectionListKeys);
  const msaSelectionList: SelectionListItem<'msa'>[] | undefined =
    selectionLists?.msa;
  const stateSelectionList: SelectionListItem<'state'>[] | undefined =
    selectionLists?.state;

  const { filters } = useFilters();

  const extent = computeExtent(points);

  const shouldUseOverviewTooltip =
    (metric === 'headcount' && !!primaryDimension) || metric === 'td_users';

  const boundingBox = useMemo(() => {
    const hasActiveFilters = hasActiveGeographyFilters(filters);
    if (hasActiveFilters) {
      const geographySelections = getNormalizedGeographySelections(
        filters,
        points
      );
      return calculateBounds({ geographySelections });
    } else {
      return calculateBounds({ points });
    }
  }, [filters, points]);

  const choroplethFeatureBuilder = useMemo(
    () => createChoroplethFeatureBuilder(points),
    [points]
  );

  const enabledConfigs = useMemo(() => {
    const allConfigs = LAYER_CONFIGS;
    return geographyMode === 'administrative'
      ? allConfigs.filter((c) => c.id !== LocationType.METRO_AREA)
      : allConfigs.filter(
          (c) => c.id !== LocationType.STATE && c.id !== LocationType.COUNTY
        );
  }, [geographyMode]);

  const enabledLayerIds = useMemo(
    () => enabledConfigs.map((c) => c.fillLayerId),
    [enabledConfigs]
  );

  const { initializeTooltipHandlers, tooltipState, handleTooltipResize } =
    useOverviewTooltip(primaryDimension, {
      enabled: shouldUseOverviewTooltip,
      layerIds: enabledLayerIds,
      buildFeature: choroplethFeatureBuilder,
      metric,
    });

  const tooltipPortal = useTooltipPortal(
    shouldUseOverviewTooltip ? tooltipState : null,
    handleTooltipResize
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    let initialBounds: mapboxgl.LngLatBoundsLike | null = null;
    if (boundingBox && !isCenterZoomBounds(boundingBox)) {
      initialBounds = boundingBox;
    }

    const mapOptions: mapboxgl.MapOptions = {
      container: mapContainerRef.current,
      style:
        'mapbox://styles/reveliolabs/cmgh3e8x6001v01sogcc11itl?optimize=true',
      performanceMetricsCollection: false,
      projection: 'mercator',
      minZoom: MIN_ZOOM,
      maxZoom: getMaxZoom(points),
      attributionControl: false,
      renderWorldCopies: false,
      maxBounds: [
        [WORLD_BOUNDS.minLon, WORLD_BOUNDS.minLat],
        [WORLD_BOUNDS.maxLon, WORLD_BOUNDS.maxLat],
      ],
      dragRotate: false,
      touchPitch: false,
      preserveDrawingBuffer: isReport,
    };

    if (initialBounds) {
      mapOptions.bounds = initialBounds;
      mapOptions.fitBoundsOptions = {
        padding: 20,
        duration: 0,
        maxZoom: getMaxZoom(points),
        minZoom: MIN_ZOOM,
      };
    } else {
      mapOptions.center = [10, 24];
      mapOptions.zoom = clampZoom(1.2, MIN_ZOOM, getMaxZoom(points));
    }

    try {
      const map = new mapboxgl.Map(mapOptions);
      mapRef.current = map;

      // Speed up zoom rate of mouse wheel and trackpad
      map.scrollZoom.setWheelZoomRate(1 / 360);
      map.scrollZoom.setZoomRate(1 / 80);

      map.on('load', () => {
        const renderer = new ChoroplethRenderer(geographyMode);
        rendererRef.current = renderer;

        const initOptions = shouldUseOverviewTooltip
          ? {
              initializeTooltipHandlers,
              interactive,
            }
          : { interactive };

        renderer.init(map, initOptions);

        setIsMapReady(true);
      });

      const resizeObserver = new ResizeObserver(() => {
        setTimeout(() => {
          if (mapRef.current && mapRef.current.getContainer()) {
            mapRef.current.resize();
          }
        }, 0);
      });
      resizeObserver.observe(mapContainerRef.current);

      return () => {
        resizeObserver.disconnect();

        if (mapRef.current) {
          rendererRef.current?.destroy();
          mapRef.current.remove();
          mapRef.current = null;
          rendererRef.current = null;
        }
      };
    } catch (error) {
      const diag = getWebGLDiagnostics();
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      setWebGLError(errorMessage);
      isSourceReady?.(true);

      const reasonStr = diag.errorMessage
        ? ` | reason: ${diag.errorMessage}`
        : '';
      const diagMessage = `WebGL initialization failed: ${errorMessage} | contextType: ${diag.contextType} | vendor: ${diag.vendor} | renderer: ${diag.renderer} | hasDebugRendererInfo: ${diag.hasDebugRendererInfo}${reasonStr}`;

      kibanaLogger?.error(diagMessage, {
        context: 'GenericMap.WebGLInit',
        url: window.location.href,
      });

      return undefined;
    }
  }, [geographyMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady || isApplyingRemoteViewport.current) return;

    if (!boundingBox) return;

    const maxZoom = getMaxZoom(points);

    if (isCenterZoomBounds(boundingBox)) {
      const clampedZoom = clampZoom(boundingBox.zoom, MIN_ZOOM, maxZoom);
      const clampedCenter: [number, number] = [
        Math.max(
          WORLD_BOUNDS.minLon,
          Math.min(WORLD_BOUNDS.maxLon, boundingBox.center[0])
        ),
        Math.max(
          WORLD_BOUNDS.minLat,
          Math.min(WORLD_BOUNDS.maxLat, boundingBox.center[1])
        ),
      ];
      map.easeTo({
        center: clampedCenter,
        zoom: clampedZoom,
        essential: true,
        duration: 0,
      });
    } else {
      const camera = map.cameraForBounds(boundingBox, {
        padding: 20,
        maxZoom,
        minZoom: MIN_ZOOM,
      });

      if (!camera) return;

      map.fitBounds(boundingBox, {
        padding: 20,
        essential: true,
        duration: 0,
        maxZoom,
        minZoom: MIN_ZOOM,
      });
    }
  }, [boundingBox, isMapReady, points]);

  useSyncViewport({
    mapRef,
    mapIdRef,
    isEnabled: syncViewport,
    isReady: isMapReady,
    isApplyingRemoteViewportRef: isApplyingRemoteViewport,
  });

  // Update renderer when data/metric/zoom changes
  useEffect(() => {
    const map = mapRef.current;
    if (!isMapReady || !map || !rendererRef.current) return;

    const effectiveMapOptions =
      normalization || (transformation && transformation !== 'none')
        ? { normalization, transformation }
        : undefined;

    rendererRef.current.update(points, metric, extent, {
      mapOptions: effectiveMapOptions,
      msaSelectionList,
      stateSelectionList,
    });
    isSourceReady?.(true);
  }, [
    points,
    metric,
    extent,
    isMapReady,
    normalization,
    transformation,
    msaSelectionList,
    stateSelectionList,
    isSourceReady,
  ]);

  if (webGLError) {
    return (
      <Box
        data-testid={testId}
        width="100%"
        height="100%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        padding={4}
      >
        <Text textAlign="center" fontSize="lg" fontWeight="bold">
          Unable to Load Map
        </Text>
        <Text textAlign="center" fontSize="sm" marginTop={2}>
          Your browser could not initialize WebGL, which is required to display
          the map.
        </Text>
        <Text textAlign="center" fontSize="sm" marginTop={2}>
          Please check that WebGL (graphics acceleration) is enabled in your
          browser settings.
        </Text>
        <Text textAlign="center" fontSize="sm" marginTop={2}>
          <a
            href="https://get.webgl.org/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#3182ce', textDecoration: 'underline' }}
          >
            Check WebGL support
          </a>
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Box data-testid={testId} width="100%" height="100%">
        <Box
          ref={mapContainerRef}
          className={styles.mapContainer}
          data-testid="mapbox-map-container"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          sx={{
            borderRadius: '0.375rem',
            overflow: 'hidden',
          }}
        ></Box>
      </Box>
      {tooltipPortal}
    </>
  );
};
